import * as React from 'react';
import { useSelector } from 'react-redux';
import * as ReactDOMServer from 'react-dom/server';
import { IMapGeoObject } from '@cian/frontend-newbuilding-map-component';
import { debounce } from '@cian/newbuilding-utils';
import { Map as ProjectsMapComponent } from '../../components/Map';
import { getFilterRegionId, getMapApiKey, getNewBuildings, getRegionCenterCoordinates } from '../../selectors';
import { NewBuildingMapBalloon } from '../NewBuildingMapBalloon';
import { getPins } from './helpers';
import { trackMapPinClick, trackSeeAllClick, trackShowPhoneBalloon } from './tracking';
import { prepareTrackingEvent } from './helpers/prepareTrackingEvent';
import { handleSeeAllLink } from './helpers/handleSeeAllLink';
import { handleNewbuildingPhone } from './helpers/handleNewbuildingPhone';
import { getDeveloperId } from '../../selectors/developerV2';
import { getPinImage } from '../../selectors/developerV2/getPinImage';

export function Map() {
  const apiKey = useSelector(getMapApiKey);
  const developerId = useSelector(getDeveloperId);

  const {
    data: { hash, list },
  } = useSelector(getNewBuildings);

  const { center, zoom } = useSelector(getRegionCenterCoordinates);
  const regionId = useSelector(getFilterRegionId);
  const pinImage = useSelector(getPinImage);

  const newBuildingsIds = React.useMemo(() => list.map(newBuilding => String(newBuilding.id)), [list]);
  const [pinTypes, setPinTypes] = React.useState<string[]>([]);

  const handleGeoObjectsFirstFetch = (): Promise<IMapGeoObject[]> => {
    const pins = getPins({ newBuildings: list, defaultPinImage: pinImage });

    return Promise.resolve(pins);
  };

  const handleNewBuildingBalloon = React.useCallback(
    (geoObject: IMapGeoObject) => {
      const newBuilding = hash[geoObject.options.id];

      /* istanbul ignore if */
      if (!newBuilding) {
        return { html: Promise.reject(new Error('Не верный тип пина!')) };
      }

      return {
        html: Promise.resolve(ReactDOMServer.renderToStaticMarkup(<NewBuildingMapBalloon newBuilding={newBuilding} />)),
        closeSelector: '#close',
      };
    },
    [hash],
  );

  const handleMapPinClick = React.useCallback(
    ({ options: { id } }: IMapGeoObject) => {
      const event = prepareTrackingEvent({
        mode: 'newBuildings',
        geoId: id,
        newbuildings: list,
      });

      /* istanbul ignore next */
      const onShowNewbuildingPhoneClick = () =>
        trackShowPhoneBalloon({
          newbuilding: hash[event.id],
          regionId,
          position: event.position,
          developerId,
        });

      /* istanbul ignore next */
      const onSeeAllLink = () =>
        trackSeeAllClick({
          newbuilding: hash[event.id],
          regionId,
          position: event.position,
          developerId,
        });

      trackMapPinClick({
        pinType: 'newBuildings',
        newbuilding: hash[event.id],
        regionId,
        position: event.position,
        developerId,
      });
      handleNewbuildingPhone({
        mode: 'newBuildings',
        newbuilding: hash[event.id],
        onShowPhoneClick: onShowNewbuildingPhoneClick,
      });

      handleSeeAllLink('newBuildings', onSeeAllLink);
    },
    [list, developerId, hash, regionId],
  );

  const debouncedHandleMapPinClick = debounce(200, handleMapPinClick);

  React.useEffect(() => {
    setPinTypes(newBuildingsIds);
  }, [newBuildingsIds]);

  return (
    <ProjectsMapComponent
      onGeoObjectClick={debouncedHandleMapPinClick}
      apiKey={apiKey}
      pinCoordinates={center}
      zoom={zoom}
      currentTypes={pinTypes}
      onGeoObjectsFirstFetch={handleGeoObjectsFirstFetch}
      onCustomBalloonHtmlFetch={handleNewBuildingBalloon}
    />
  );
}
